import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, type AdminClient } from '../api/admin';
import { errorMessage, getAdmin, saveAdminConfig, setReviewer, type AdminState } from '../api/prReview';

// PR review setup for one client: the GitHub label that puts a PR in the queue, the statement an
// approval agrees to, and the staff designated to record the business review. Built like UAT's signers,
// with staff in place of client users. Reachable by direct URL, so the client is fetched and named.

const box: React.CSSProperties = { display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 };
const input: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 8px', font: 'inherit', fontSize: 13 };
const secondary: React.CSSProperties = { background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', color: 'var(--mi-text)', padding: '5px 10px', fontSize: 13 };
const muted: React.CSSProperties = { margin: 0, fontSize: 13, color: 'var(--mi-muted)' };

export default function AdminModulePrReview() {
  const { clientId } = useParams();
  const auth = useAuth();
  const tokenRef = useRef(auth.getToken);
  tokenRef.current = auth.getToken;
  const getToken = useCallback(() => tokenRef.current(), []);

  const [client, setClient] = useState<AdminClient | null>(null);
  const [state, setState] = useState<AdminState | null>(null);
  const [label, setLabel] = useState('');
  const [statement, setStatement] = useState('');
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    const t = await getToken();
    const [s, c] = await Promise.all([getAdmin(clientId, t), adminApi.getClient(t, clientId)]);
    setState(s);
    setClient(c);
    setLabel(s.config.reviewLabel);
    setStatement(s.config.statementText ?? '');
    setRoles(Object.fromEntries(s.reviewers.map((x) => [x.userId, x.roleLabel])));
  }, [clientId, getToken]);
  useEffect(() => { load().catch((e) => setMsg({ ok: false, text: errorMessage(e) })); }, [load]);

  const act = async (ok: string, fn: () => Promise<unknown>) => {
    try { await fn(); await load(); setMsg({ ok: true, text: ok }); } catch (e) { setMsg({ ok: false, text: errorMessage(e) }); }
  };

  // Designated reviewers who have since lost access to the client stay listed, so they can be removed.
  const people = state ? [...state.candidates, ...state.reviewers.filter((r) => !state.candidates.some((c) => c.userId === r.userId))] : [];

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
      <div>
        <Link to={`/admin/clients/${clientId}`} style={{ color: 'var(--mi-purple)', fontSize: 13 }}>← {client?.name ?? 'Client'}</Link>
        <h2 style={{ margin: '6px 0 0', color: 'var(--mi-navy)' }}>PR Review: {client?.name ?? '…'}</h2>
        <p style={muted}>
          For this client&apos;s designated reviewers only. A pull request in the client&apos;s UI sign-off repo appears when its author or a reviewer adds the label below on GitHub.
          {client && <> Open the module: <Link to={`/${client.slug}/pr-review`} style={{ color: 'var(--mi-purple)' }}>/{client.slug}/pr-review</Link>.</>}
        </p>
      </div>
      {msg && <div role="status" style={{ fontSize: 13, color: msg.ok ? 'var(--mi-navy)' : '#b3261e' }}>{msg.text}</div>}
      {!state ? <p style={muted}>Loading…</p> : (
        <>
          <section style={box} aria-label="Label and statement">
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Review label on GitHub
              <input style={input} value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Statement an approval agrees to (blank: &ldquo;These changes are what we want.&rdquo;)
              <textarea style={{ ...input, minHeight: 56 }} value={statement} onChange={(e) => setStatement(e.target.value)} maxLength={4000} />
            </label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button style={secondary} disabled={!label.trim()}
                onClick={() => act('Saved.', async () => saveAdminConfig(clientId!, { reviewLabel: label.trim(), statementText: statement.trim() || null }, await getToken()))}>
                Save
              </button>
              <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>Statement version {state.config.statementVersion}. Changing the wording starts a new version; past reviews keep theirs.</span>
            </div>
          </section>

          <section style={box} aria-label="Reviewers">
            <div style={{ fontSize: 13, fontWeight: 600 }}>Reviewers</div>
            <p style={muted}>Only the reviewers listed here see PR review for this client at all, and only they record the business review. That applies to admins too: an admin who is not listed here neither sees the module nor its pull requests.</p>
            {people.length === 0 ? <p style={muted}>No staff can reach this client yet. Assign client access first.</p> : (
              <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {people.map((u) => {
                    const current = state.reviewers.find((x) => x.userId === u.userId);
                    const value = roles[u.userId] ?? '';
                    return (
                      <tr key={u.userId} style={{ borderTop: '1px solid var(--mi-border)' }}>
                        <td style={{ padding: 6 }}>{u.name ?? u.email}<div style={{ color: 'var(--mi-muted)', fontSize: 12 }}>
                          {u.email}
                          {'staffRole' in u && <> · {u.staffRole === 'admin' ? 'staff admin (reaches every client)' : 'client admin assigned to this client'}</>}
                          {!('staffRole' in u) && <> · no longer staff with access to this client</>}
                        </div></td>
                        <td style={{ padding: 6 }}><input style={input} placeholder="Reviews as, e.g. Business owner" value={value} onChange={(e) => setRoles({ ...roles, [u.userId]: e.target.value })} /></td>
                        <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                          <button style={secondary} disabled={!value.trim() || value.trim() === current?.roleLabel}
                            onClick={() => act('Reviewer saved.', async () => setReviewer(clientId!, u.userId, value.trim(), await getToken()))}>
                            {current ? 'Update' : 'Make reviewer'}
                          </button>{' '}
                          {current && <button style={secondary} onClick={() => act('Reviewer removed.', async () => setReviewer(clientId!, u.userId, null, await getToken()))}>Remove</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
